import {
    CameraModel,
    ControlModel,
    InputModel,
    UserModel,
    UpdateUserModel,
    ControlTypeModel,
    SensorTypeModel,
    CameraBrandModel,
    MediaModel,
    InputTypeModel,
    RelationModel,
    GatewayCommand,
    GatewayInfoModel,
} from "../types/control";
import {BridgeDeviceModel} from "../types/bridge-device";
import {ClientTask} from "./client-task";
import {
    AlarmStateModel,
    AreaModel,
    ConfigModel,
    CreateMediaDeviceModel,
    MediaInputModel,
    VirtualControlModel,
    ScenarioModel,
    ScenarioTriggerModel,
    HomepageItemModel,
    SoundState,
    UpdateFCMPushTokenRequest,
    MenuEntity,
    STPUIEntity,
    LanguageModel,
    IftttState,
    ScreenState,
    LogListType,
    UserLogListType,
    ConnectionIcons, IFTTTResponse,
    //ScreenPinCode
} from "../store/control/state";
import {WS} from "../utils/ws";
import {SettingsModel} from "../store/settings/state";
import {IntercomModel} from "../types/intercom";
import {DoorModel} from "../types/door";
import {RPCService as BaseRPCService} from "../base/RPCService";
import {Configs} from "../configs";
import {MediaFileModel} from "../store/ui/state";
import {GatewayModel} from "../types/gateway";
import {DoorBrandModel} from "../types/door-brand";
import {BridgeDeviceTypeModel} from "../types/bridge-device-type";
import {WifiNetworkModel} from "../types/wifi-network";
import {SystemInfo} from "../types/system-info";
import {LocationModel} from "../types/location";
import {TemperatureMessage} from "../store/temperature/state";
import {ConciergeModel} from "../types/concierge";
import {ConciergeTypeModel} from "../types/concierge-type";
import {RgbColor} from "../components/ColorPicker/types";

export class RPCService extends BaseRPCService<ClientTask> {
    public constructor(client: ClientTask, websocket: WS) {
        super(client, websocket);
    }

    public test() {
        return this.ask("Test");
    }

    public IncomingOuterPanelRing(name: number, command: string) {
        return this.ask("IncomingOuterPanelRing", {name, command});
    }

    public SetClickSound(status: boolean) {
        return this.ask("SetClickSound", status)
    }

    public IncomingFlatFrontRing(name: number, command: string) {
        return this.ask("IncomingFlatFrontRing", {name, command});
    }

    public ping() {
        return this.ask("Ping");
    }

    public sendBrokenLink(link: string) {
        return this.ask<string>("SendBrokenLink", {link});
    }

    public testNotification(os: string, token: string) {
        return this.ask<string>("TestNotification", {os, token});
    }

    public connectToMachine(
        address: string,
        username: string,
        password: string,
        id: string
    ) {
        return this.ask<string>("ConnectToMachine", {
            address,
            username,
            password,
            id,
        });
    }

    public beforeConnectToMachine(
        address: string,
        username: string,
        password: string
    ) {
        return this.ask<{ data: GatewayInfoModel[] }>("BeforeConnectToMachine", {
            address,
            username,
            password,
        });
    }

    public connectToLocalMachine() {
        return this.ask<string>("ConnectToLocalMachine");
    }

    public downloadDB() {
        return this.ask<{ data: string }>("DownloadDB").then((r) => r.data);
    }

    public uploadDB(data: unknown) {
        return this.ask<{ data: string }>("UploadDB", {data});
    }

    public resetDB() {
        return this.ask("ResetDB");
    }

    public getUserList() {
        return this.ask<{ data: UserModel[] }>("GetUserList").then((r) =>
            r.data.sort((a, b) => (a.id > b.id ? -1 : 1))
        );
    }

    public getSTPUIList() {
        return this.ask<{ data: STPUIEntity[] }>("GetSTPUIList").then((r) =>
            r.data.sort((a, b) => (a.id > b.id ? -1 : 1))
        );
    }

    public getLanguageList() {
        return this.ask<{ data: LanguageModel[] }>("GetLanguageList").then((r) =>
            r.data.sort((a, b) => (a.id > b.id ? -1 : 1))
        );
    }

    public getConciergeList() {
        return this.ask<{ data: ConciergeModel[] }>("GetConciergeList").then((r) =>
            r.data.sort((a, b) => (a.id > b.id ? -1 : 1))
        );
    }

    public getConciergeTypeList() {
        return this.ask<{ data: ConciergeTypeModel[] }>(
            "GetConciergeTypeList"
        ).then((r) => r.data.sort((a, b) => (a.id > b.id ? -1 : 1)));
    }

    public getControlTypeList() {
        return this.ask<{ data: ControlTypeModel[] }>("GetControlTypeList").then(
            (r) =>
                r.data
                    .map((type) => {
                        for (const name in type.image_set) {
                            // noinspection JSUnfilteredForInLoop
                            type.image_set[name] =
                                Configs.IMAGE_PREFIX + type.image_set[name];
                        }

                        return type;
                    })
                    .sort((a, b) => (a.id > b.id ? -1 : 1))
        );
    }

    public getSensorTypeList() {
        return this.ask<{ data: SensorTypeModel[] }>("GetSensorTypeList").then(
            (r) =>
                r.data
                    .map((type) => {
                        for (const name in type.image_set) {
                            // noinspection JSUnfilteredForInLoop
                            type.image_set[name] =
                                Configs.IMAGE_PREFIX + type.image_set[name];
                        }

                        return type;
                    })
                    .sort((a, b) => (a.id > b.id ? -1 : 1))
        );
    }

    public getControlList() {
        return this.ask<{ data: ControlModel[] }>("GetControlList")
            .then((r) => r.data != null ? r.data.sort((a, b) => a.name.localeCompare(b.name)) : r.data) //to do (ordered)
            .then((controls) => controls != null &&
                controls.map((control) => {
                    if (control.temperature_settings != null) {
                        control.temperature_settings.whole =
                            control.temperature_settings.whole || 14;
                        if (control.temperature_settings.whole === 14) {
                            control.temperature_settings.fraction = 5;
                        }
                    }

                    return control;
                })
            );
    }

    public getCameraList() {
        return this.ask<{ data: CameraModel[] }>("GetCameraList").then((r) =>
            r.data.sort((a, b) => a.name.localeCompare(b.name))
        );
    }

    public getCameraBrandList() {
        return this.ask<{ data: CameraBrandModel[] }>("GetCameraBrandList").then(
            (r) => r.data.sort((a, b) => (a.id > b.id ? -1 : 1))
        );
    }

    public getMediaFileList() {
        return this.ask<{ data: MediaFileModel[] }>("GetMediaFileList").then((r) =>
            r.data.sort((a, b) => (a.id > b.id ? -1 : 1))
        );
    }

    public getScenarioList() {
        return this.ask<{ data: ScenarioModel[] }>("GetScenarioList").then((r) =>
            r.data.sort((a, b) => a.name.localeCompare(b.name))
        );
    }

    public getScenarioTriggerList() {
        return this.ask<{ data: ScenarioTriggerModel[] }>(
            "GetScenarioTriggerList"
        ).then((r) => r.data.sort((a, b) => a.name.localeCompare(b.name)));
    }

    public getDoorList() {
        return this.ask<{ data: DoorModel[] }>("GetDoorList")
            .then((r) => r.data.sort((a, b) => a.name.localeCompare(b.name)))
            .then((r) => r.map((d) => ({...d, toString: () => d.id})));
    }

    public getDoorBrandList() {
        return this.ask<{ data: DoorBrandModel[] }>("GetDoorBrandList").then((r) =>
            r.data.sort((a, b) => (a.id > b.id ? -1 : 1))
        );
    }

    public getIntercomList() {
        return this.ask<{ data: IntercomModel[] }>("GetIntercomList").then((r) =>
            r.data.sort((a, b) => a.name.localeCompare(b.name))
        );
    }

    public getInputTypeList() {
        return this.ask<{ data: InputTypeModel[] }>("GetInputTypeList")
            .then((r) => r.data.filter((c) => !c.registered_by_system))
            .then((r) => r.sort((a, b) => (a.id > b.id ? 1 : -1)));
    }

    public getInputList() {
        return this.ask<{ data: InputModel[] }>("GetInputList")
            .then((r) => r.data.filter((c) => !c.registered_by_system))
            .then((r) => r.sort((a, b) => a.name.localeCompare(b.name)));
    }

    public getAreaList() {
        return this.ask<{ data: AreaModel[] }>("GetAreaList").then((r) =>
            r.data.sort((a, b) => a.name.localeCompare(b.name))
        );
    }

    public getMediaList() {
        return this.ask<{ data: MediaModel[] }>("GetMediaList").then((r) =>
            r.data.sort((a, b) => a.name.localeCompare(b.name))
        );
    }

    public getWifiNetworkList(gatewayId: string) {
        return this.ask<{ data: WifiNetworkModel[] }>(
            "GetWifiNetworkList",
            gatewayId
        ).then((r) => r.data.sort((a, b) => (a.name > b.name ? 1 : -1)));
    }

    public getLocationList() {
        return this.ask<{ data: LocationModel[] }>("GetLocationList").then((r) =>
            r.data.sort((a, b) => a.name.localeCompare(b.name))
        );
    }

    public getTemperature() {
        return this.ask<TemperatureMessage>("GetTemperature");
    }

    public connectWifiNetwork(gatewayId: string, name: string, password: string) {
        return this.ask("ConnectWifiNetwork", {
            gateway_id: gatewayId,
            name,
            password,
        });
    }

    public disconnectWifiNetwork(gatewayId: string) {
        return this.ask("DisconnectWifiNetwork", {gateway_id: gatewayId});
    }

    public getMediaInputList() {
        return this.ask<{ data: MediaInputModel[] }>("GetMediaInputList").then(
            (r) => r.data.sort((a, b) => (a.id > b.id ? -1 : 1))
        );
    }

    public getVirtualControlList() {
        return this.ask<{ data: VirtualControlModel[] }>("GetVirtualControlList")
            .then((r) => r.data /*.filter(c => !c.registered_by_system)*/)
            .then((r) => r.sort((a, b) => (a.id > b.id ? -1 : 1)));
    }

    public getHomepageItemList() {
        return this.ask<{ data: HomepageItemModel[] }>("GetHomepageItemList").then(
            (r) => r.data.sort((a, b) => (a.order > b.order ? -1 : 1))
        );
    }

    public getBridgeDeviceList() {
        return this.ask<{ data: BridgeDeviceModel[] }>("GetBridgeDeviceList").then(
            (r) => r.data.sort((a, b) => (a.id > b.id ? -1 : 1))
        );
    }

    public getBridgeDeviceTypeList() {
        return this.ask<{ data: BridgeDeviceTypeModel[] }>(
            "GetBridgeDeviceTypeList"
        ).then((r) =>
            r.data
                .map((type) => {
                    for (const name in type.image_set) {
                        // noinspection JSUnfilteredForInLoop
                        type.image_set[name] = Configs.IMAGE_PREFIX + type.image_set[name];
                    }

                    return type;
                })
                .sort((a, b) => a.name.localeCompare(b.name))
        );
    }

    public getGatewayList() {
        return this.ask<{ data: GatewayModel[] }>("GetGatewayList").then((r) =>
            r.data.sort((a, b) => a.name.localeCompare(b.name))
        );
    }

    public getMenuList() {
        return this.ask<{ data: MenuEntity[] }>("GetMenuList").then((r) => r.data);
    }

    public getAlarmState() {
        return this.ask<AlarmStateModel>("GetAlarmState");
    }

    public getSoundState() {
        return this.ask<SoundState>("GetSoundState");
    }

    public getIftttState() {
        return this.ask<IftttState>("GetIFTTTState");
    }

    public getSystemInfo() {
        return this.ask<SystemInfo>("GetSystemInfo");
    }

    public scanDevices(gateway_id: string, is_auto: boolean) {
        return this.ask("ScanDevices", {gateway_id, is_auto});
    }

    public findDevice(
        gateway_id: string,
        unique_device_id: string,
        is_stop: boolean
    ) {
        return this.ask("FindDevice", {gateway_id, unique_device_id, is_stop});
    }

    public matchDevice(
        gateway_id: string,
        unique_device_id: string,
        device_id: string
    ) {
        return this.ask("MatchDevice", {gateway_id, unique_device_id, device_id});
    }

    public reMatchDevice(
        gateway_id: string,
        unique_device_id: string,
        device_id: string
    ) {
        return this.ask("ReMatchDevice", {
            gateway_id,
            unique_device_id,
            device_id,
        });
    }

    public resetDevice(gateway_id: string, unique_device_id: string) {
        return this.ask("ResetDevice", {gateway_id, unique_device_id});
    }

    public versionInfo(gateway_id: string, unique_device_id: string) {
        return this.ask("CheckDeviceVersion", {gateway_id, unique_device_id})
    }
    public rssiInfo(gateway_id: string, unique_device_id: string) {
        return this.ask("CheckDeviceRSSI", {gateway_id, unique_device_id})
    }

    public getSettings() {
        return this.ask<SettingsModel>("GetSettings");
    }

    public getConfigs() {
        return this.ask<ConfigModel>("GetConfigs");
    }

    public updateSoundLevel = (level: number) => {
        return this.ask<string>("UpdateSoundLevel", level);
    };

    public updateSoundState = (soundState: SoundState) => {
        return this.ask<string>("UpdateSoundState", soundState);
    };

    public setIftttState = (iftttState: IftttState) => {
        return this.ask<string>("SetIFTTTSettings", iftttState).catch(console.log);
    };

    public updateFCMPushToken = (request: UpdateFCMPushTokenRequest) => {
        return this.ask<string>("UpdateFCMPushToken", request);
    };

    public updateControlValue = (id: string, value: number) => {
        return this.ask<string>("UpdateControlValue", {id, value})
            .catch(console.log)
            .then(() => true);
    };

    public updateRGBControlValue = (id: string, value: number, color: RgbColor) => {
        return this.ask<string>("UpdateRGBControlValue", {id, value, color})
            .catch(console.log)
            .then(() => true);
    };

    public updateScenarioTriggerControl = (id: string, scenarioId: string) => {
        return this.ask<string>("UpdateScenarioTriggerControl", {
            id,
            scenario_id: scenarioId,
        });
    };

    public updateTemperature = (id: string, whole: number, fraction: number) => {
        this.ask<string>("UpdateTemperature", {id, whole, fraction}).catch(
            console.log
        );
    };

    public changeHeatingMode = (id: string) => {
        this.ask<string>("ChangeHeatingMode", id).catch(console.log);
    };

    public mediaButtonPressed(
        id: string,
        slot: number,
        extra: { [key: string]: any } = {}
    ) {
        this.ask<string>("MediaButtonPressed", {id, slot, ...extra}).catch(
            console.log
        );
    }

    public registerMediaInput(id: string) {
        return this.ask<string>("RegisterMediaInput", id);
    }

    public updateSensorStatus(
        id: string,
        is_active: boolean,
        is_control: boolean
    ) {
        this.ask<string>("UpdateSensorStatus", {id, is_active, is_control}).catch(
            console.log
        );
    }

    public updateNotificationStatus(
        id: string,
        is_notification: boolean
    ) {
        this.ask<string>("UpdateNotificationStatus", {
            id,
            is_notification
        }).catch(console.log);
    }

    public updateSensorCameraMatch(
        sensorId: string,
        cameraId: string
    ) {
        this.ask<string>("UpdateSensorCameraMatch", {
            sensor_id: sensorId,
            camera_id: cameraId
        }).catch(console.log);
    }

    public sendUartMessage(message: string) {
        this.ask<string>("SendUartMessage", message).catch(console.log);
    }

    public answerIntercomRequest(status: string) {
        this.ask("AnswerIntercomRequest", status).catch(console.log);
    }

    public callIntercom(request: string) {
        return this.ask("CallIntercom", request).catch(console.log);
    }

    public createConciergeRequest(id: string, message: string) {
        return this.ask("CreateConciergeRequest", {id, message});
    }

    public openDoor(id: string) {
        this.ask("OnDoorOpen", id).catch(console.log);
    }

    public updateScenario(scenarioModel: ScenarioModel) {
        return this.ask("UpdateScenario", scenarioModel)
            .then(() => true)
            .catch(() => false);
    }

    public updateUser(userModel: UpdateUserModel) {
        return this.ask("UpdateUser", userModel);
    }

    public updateSTPUI(stpUIEntity: STPUIEntity) {
        return this.ask("UpdateSTPUI", stpUIEntity);
    }

    public updateGateway(gatewayModel: GatewayModel) {
        return this.ask("UpdateGateway", gatewayModel);
    }

    public updateConfigs(configModel: ConfigModel) {
        return this.ask("UpdateConfigs", configModel);
    }

    public updateBridgeDevice(bridgeDevice: BridgeDeviceModel) {
        return this.ask("UpdateBridgeDevice", bridgeDevice);
    }

    public updateInput(input: InputModel) {
        return this.ask("UpdateInput", input);
    }

    public updateArea(area: AreaModel) {
        return this.ask("UpdateArea", area);
    }

    public updateIntercom(e: IntercomModel) {
        return this.ask("UpdateIntercom", e);
    }

    public updateHomepageItem(e: HomepageItemModel) {
        return this.ask("UpdateHomepageItem", e);
    }

    public updateMenu(e: MenuEntity) {
        return this.ask("UpdateMenu", e);
    }

    public updateDoor(e: DoorModel) {
        return this.ask("UpdateDoor", e);
    }

    public updateConcierge(e: ConciergeModel) {
        return this.ask("UpdateConcierge", e);
    }

    public updateDoorBrand(e: DoorBrandModel) {
        return this.ask("UpdateDoorBrand", e);
    }

    public updateMediaInput(input: MediaInputModel) {
        return this.ask("UpdateMediaInput", input);
    }

    public updateControl(controlModel: ControlModel) {
        return this.ask<string>("UpdateControl", controlModel);
    }

    public updateControlType(controlTypeModel: ControlTypeModel<unknown>) {
        for (const name in controlTypeModel.image_set) {
            // noinspection JSUnfilteredForInLoop
            controlTypeModel.image_set[name] = controlTypeModel.image_set[
                name
                ].replace(Configs.IMAGE_PREFIX, "");
        }

        return this.ask<string>("UpdateControlType", controlTypeModel);
    }

    public updateVirtualControl(virtualControl: VirtualControlModel) {
        return this.ask("UpdateVirtualControl", virtualControl);
    }

    public updateCamera(camera: CameraModel) {
        return this.ask("UpdateCamera", camera);
    }

    public updateCameraBrand(cameraBrand: CameraBrandModel) {
        return this.ask("UpdateCameraBrand", cameraBrand);
    }

    public updateScenarioTrigger(scenarioTriggerModel: ScenarioTriggerModel) {
        return this.ask("UpdateScenarioTrigger", scenarioTriggerModel)
            .then(() => true)
            .catch(() => false);
    }

    public createMediaDevice(
        mediaDevice: CreateMediaDeviceModel
    ): Promise<MediaModel> {
        return this.ask<MediaModel>("CreateMediaDevice", mediaDevice);
    }

    public getRelations(relational_id: string, type: string) {
        return this.ask<{ data: RelationModel[] }>("GetRelations", {
            relational_id,
            type,
        });
    }

    public deleteMedia(id: string) {
        return this.ask("DeleteMedia", id);
    }

    public deleteControl(id: string) {
        return this.ask("DeleteControl", id);
    }

    public deleteBridgeDevice(id: string) {
        return this.ask("DeleteBridgeDevice", id);
    }

    public deleteInput(id: string) {
        return this.ask("DeleteInput", id);
    }

    public deleteArea(id: string) {
        return this.ask("DeleteArea", id);
    }

    public deleteIntercom(id: string) {
        return this.ask("DeleteIntercom", id);
    }

    public deleteHomepageItem(id: string) {
        return this.ask("DeleteHomepageItem", id);
    }

    public deleteMenu(id: string) {
        return this.ask("DeleteMenu", id);
    }

    public deleteDoor(id: string) {
        return this.ask("DeleteDoor", id);
    }

    public deleteConcierge(id: string) {
        return this.ask("DeleteConcierge", id);
    }

    public deleteDoorBrand(id: string) {
        return this.ask("DeleteDoorBrand", id);
    }

    public deleteMediaInput(id: string) {
        return this.ask("DeleteMediaInput", id);
    }

    public deleteVirtualControl(id: string) {
        return this.ask("DeleteVirtualControl", id);
    }

    public deleteCamera(id: string) {
        return this.ask("DeleteCamera", id);
    }

    public playScenario(scenarioId: string) {
        this.ask("PlayScenario", scenarioId).catch(console.log);
    }

    public stopScenario() {
        this.ask("StopScenario").catch(console.log);
    }

    public deleteScenario(scenarioId: string) {
        return this.ask("DeleteScenario", scenarioId).catch(console.log);
    }

    public deleteUser(userId: string) {
        return this.ask("DeleteUser", userId);
    }

    public deleteSTPUI(stpUIId: string) {
        return this.ask("DeleteSTPUI", stpUIId);
    }

    public deleteGateway(gatewayId: string) {
        return this.ask("DeleteGateway", gatewayId);
    }

    public deleteScenarioTrigger(scenarioTriggerId: string) {
        return this.ask("DeleteScenarioTrigger", scenarioTriggerId).catch(
            console.log
        );
    }

    public setAlarm(level: number, password: string) {
        return this.ask<boolean>("SetAlarm", {level, password})
            .then((response) => response)
            .catch(() => false);
    }

    public CheckAlarmPassword(password: string) {
        return this.ask<boolean>("CheckAlarmPassword", password).then(res => res);
    }

    public authenticate(username: string, password: string, security_control?: string, security_code?: string) {
        return this.ask("Authenticate", {username, password, security_control, security_code})
    }

    public updateTimeAndWeather(
        time: number,
        offset: number,
        temperature = -1,
        icon = -1
    ) {
        return this.ask("UpdateTimeAndWeather", {
            time,
            offset,
            temperature,
            icon,
        });
    }

    public broadcastTimeAndWeather() {
        return this.ask("BroadcastTimeAndWeather");
    }

    public getRTC() {
        return this.ask("GetRTC");
    }

    public clickSound() {
        return this.ask("ClickSound");
    }

    public touchEvent() {
        this.clickSoundOn()
        this.clickSound()
        return this.ask("TouchEvent");
    }


    public clickSoundOn() {
        return this.ask("ClickSoundOn");
    }

    public setDirectOutput(
        gateway_id: string,
        unique_device_id: string,
        slot: number,
        value: number
    ) {
        return this.ask("SetDirectOutput", {
            gateway_id,
            unique_device_id,
            slot,
            value,
        });
    }

    public getDirectInput(
        gateway_id: string,
        unique_device_id: string,
        slot: number
    ) {
        return this.ask<number>("GetDirectInput", {
            gateway_id,
            unique_device_id,
            slot,
        });
    }

    public manageGateway(id: string, command: GatewayCommand) {
        return this.ask("ManageGateway", {id, command});
    }

    public doLogout() {
        return this.ask("DoLogout");
    }

    public authenticateTimeout() {
        return this.ask("AuthenticateTimeout");
    }

    public acceptUartLogs(acceptUartLogs: boolean) {
        return this.ask("AcceptUartLogs", acceptUartLogs);
    }

    public groupScriptCommand(area_id: string, status: boolean) {
        return this.ask("GroupScriptCommand", {
            area_id,
            status,
            control_type: "all",
        });
    }

    public getScreenState() {
        return this.ask<ScreenState>("GetScreenState");
    }

    public getScreenPinCodeState() {
        return this.ask<ScreenState>("GetScreenPinCodeState")
    }

    public updateScreenBrightness = (brightness: number) => {
        return this.ask<string>("SetScreenBrightness", brightness)
    };

    public updateScreenSleepBrightness = (brightness: number) => {
        return this.ask<string>("SetScreenSleepBrightness", brightness)
    };

    public updateScreenSaverTime = (saverTime: number) => {
        return this.ask<string>("SetScreenSaverTime", saverTime)
    };

    public updateScreenSleepTime = (sleepTime: number) => {
        return this.ask<string>("SetScreenSleepTime", sleepTime)
    };

    public updateScreenSaverType = (saverType: string) => {
        return this.ask<string>("SetScreenSaverType", saverType)
    };

    public updateRotateType = (rotateType: string) => {
        return this.ask<string>("SetRotateType", rotateType)
    };

    public updateScreenSaverState = (saverState: boolean) => {
        return this.ask<string>("SetScreenSaverState", saverState)
    };

    public updateScreenPinCodeState = (pinScreen: boolean) => {
        return this.ask<string>("SetScreenPinCodeState", pinScreen)
    };

    public updateScreenPinCodeTime = (pinScreen: number) => {
        return this.ask<string>("SetScreenPinCodeTime", pinScreen)
    };

    public setTheme(name: string) {
        return this.ask("SetTheme", name)
    }

    public UploadTBellImage(base64Image: string) {
        return this.ask("UploadTBellImage", base64Image).then(res => res);
    }

    public GetLogList() {
        return this.ask<{ data: LogListType[] }>("GetLogList").then(r => r.data) //to do id control
    }

    public DownloadLog(data: LogListType) {
        return this.ask<{ data: string }>("DownloadLog", data).then(r => r.data)
    }

    public UpdateFanCoilTemperature = (id: string, whole: number, fraction: number, power_status: boolean, fan_speed: string, mode: boolean, ventilate: boolean) => {
        this.ask<string>("UpdateFanCoilTemperature", {
            id,
            whole,
            fraction,
            mode,
            fan_speed,
            power_status,
            ventilate
        }).catch(console.log);
    };

    public deleteMediaFile(id: string) {
        return this.ask("DeleteMediaFile", id);
    }

    public GetUserLogList() {
        return this.ask<{ data: UserLogListType[] }>("GetUserLogList").then(r => r.data)
    };

    public GetDeviceRFSignalStrength() {
        return this.ask("GetDeviceRFSignalStrength")
    };

    public GetSecurityCode() {
        return this.ask("GetSecurityCode")
    };

    public GetConnIcons() {
        return this.ask<{ data: ConnectionIcons }>("GetConnIcons").then(r => r?.data)
    };

    public ScanTemperatureSensor() {
        return this.ask("ScanTemperatureSensor");
    }

    public DefaultTemperatureSensor() {
        return this.ask("DefaultTemperatureSensor");
    }

    public ReadAllTemperatureSensor() {
        return this.ask("ReadAllTemperatureSensor");
    }

    public StopTemperatureSensor() {
        return this.ask("StopTemperatureSensor");
    }

    public LoginIFTTTCloud = (e_mail: string, password: string) => {
        return this.ask<{ data: IFTTTResponse }>("LoginIFTTTCloud",{e_mail,password}).then(r => r?.data)
    };

    public LogOutIFTTTCloud () {
        return this.ask("LogOutIFTTTCloud");
    }
}



